import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Smartphone, Shield, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewSoftTokenWizard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  const confirmCancel = () => {
    setShowCancelDialog(false);
    navigate('/dashboard');
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Validate PIN
      if (pin.length < 4 || pin.length > 8 || !/^\d+$/.test(pin)) {
        toast.error('PIN must be 4-8 digits');
        return;
      }
      
      setLoading(true);
      try {
        // Call backend to create soft token
        const response = await axios.post(`${API}/softtoken/create?username=${user.username}`, {
          pin: pin
        });

        if (response.data.success) {
          setTokenData(response.data);
          setCurrentStep(3);
        }
      } catch (error) {
        console.error('Error creating soft token:', error);
        const errorMessage = error.response?.data?.detail || 'Failed to create soft-token';
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEmailDelivery = () => {
    setDeliveryMethod('email');
  };

  const handleQRDelivery = async () => {
    try {
      const qrUrl = await QRCode.toDataURL(tokenData.token_link);
      setQrCodeUrl(qrUrl);
      setDeliveryMethod('qr');
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const handleSendEmail = async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/softtoken/send-email`, {
        email: email,
        token_link: tokenData.token_link,
        username: user.username
      });
      
      setShowEmailConfirm(true);
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmEmailSent = () => {
    setShowEmailConfirm(false);
    navigate('/dashboard');
  };

  const handleFinish = () => {
    navigate('/dashboard');
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="soft-token-wizard">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3].map((step) => (
          <div
            key={step}
            className={`h-2 w-16 rounded-full transition-colors ${
              step <= currentStep ? 'bg-emerald' : 'bg-muted'
            }`}
          />
        ))}
      </div>

      {/* Step 1: Welcome */}
      {currentStep === 1 && (
        <Card className="p-8 border border-border" data-testid="wizard-step-1">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center">
              <Smartphone className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h2 className="font-outfit font-bold text-2xl text-foreground">Create Soft-Token</h2>
              <p className="text-muted-foreground font-ibm">Step 1 of 3</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-outfit font-semibold text-lg mb-2">What is a Soft-Token?</h3>
              <p className="text-muted-foreground font-ibm leading-relaxed">
                A soft-token is a mobile authentication token that generates time-based one-time passwords (OTP) 
                for secure access to your accounts. It provides an additional layer of security beyond your username 
                and password.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-ibm font-medium text-blue-900 mb-2">
                    Mobile App Required
                  </p>
                  <p className="text-sm text-blue-800 mb-3">
                    To use your soft-token, you'll need to install the Mi-Token Authenticator app:
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <a
                      href="https://play.google.com/store/apps/details?id=com.mi_token.mi_token&hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-md text-sm font-medium text-blue-700 hover:bg-blue-50 transition-colors"
                      data-testid="android-link"
                    >
                      <Smartphone className="h-4 w-4" />
                      Download for Android
                    </a>
                    <a
                      href="https://apps.apple.com/us/app/mi-token-authenticator/id377249663"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-md text-sm font-medium text-blue-700 hover:bg-blue-50 transition-colors"
                      data-testid="ios-link"
                    >
                      <Smartphone className="h-4 w-4" />
                      Download for iOS
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="h-12 font-ibm"
                data-testid="cancel-button"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 h-12 font-ibm font-medium"
                data-testid="next-button"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 2: PIN Entry */}
      {currentStep === 2 && (
        <Card className="p-8 border border-border" data-testid="wizard-step-2">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h2 className="font-outfit font-bold text-2xl text-foreground">Set Your PIN</h2>
              <p className="text-muted-foreground font-ibm">Step 2 of 3</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <Label htmlFor="pin" className="font-ibm font-medium text-base mb-2">
                Enter PIN (4-8 digits)
              </Label>
              <Input
                id="pin"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Enter your PIN"
                className="h-14 text-lg font-mono tracking-widest"
                maxLength={8}
                data-testid="pin-input"
              />
              <p className="text-sm text-muted-foreground mt-2 font-ibm">
                This PIN will be required when using your soft-token for authentication
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="h-12 font-ibm"
                data-testid="cancel-button"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(1)}
                className="h-12 font-ibm"
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={loading || pin.length < 4}
                className="flex-1 h-12 font-ibm font-medium"
                data-testid="create-button"
              >
                {loading ? 'Creating...' : 'Create Soft-Token'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 3: Delivery Method Selection or QR/Email Display */}
      {currentStep === 3 && !deliveryMethod && (
        <Card className="p-8 border border-border" data-testid="wizard-step-3-choice">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Shield className="h-8 w-8 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-outfit font-bold text-2xl text-foreground">Soft-Token Created!</h2>
              <p className="text-muted-foreground font-ibm">Step 3 of 3</p>
            </div>
          </div>

          <div className="space-y-6">
            <p className="text-muted-foreground font-ibm text-base">
              Your soft-token has been created successfully. How would you like to activate it?
            </p>

            <div className="grid gap-4">
              <button
                onClick={handleQRDelivery}
                className="p-6 border-2 border-border rounded-lg hover:border-emerald hover:bg-emerald-50/50 transition-all text-left group"
                data-testid="qr-option"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200">
                    <Smartphone className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-outfit font-semibold text-lg">Scan QR Code</p>
                    <p className="text-sm text-muted-foreground font-ibm">
                      Scan the QR code with your Mi-Token Authenticator app
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={handleEmailDelivery}
                className="p-6 border-2 border-border rounded-lg hover:border-emerald hover:bg-emerald-50/50 transition-all text-left group"
                data-testid="email-option"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200">
                    <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-outfit font-semibold text-lg">Send via Email</p>
                    <p className="text-sm text-muted-foreground font-ibm">
                      Receive an activation link via email
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* QR Code Display */}
      {currentStep === 3 && deliveryMethod === 'qr' && (
        <Card className="p-8 border border-border" data-testid="qr-display">
          <div className="text-center space-y-6">
            <h2 className="font-outfit font-bold text-2xl text-foreground">Scan QR Code</h2>
            
            <div className="inline-block p-6 bg-white rounded-lg border-2 border-border">
              <img src={qrCodeUrl} alt="Token QR Code" className="w-64 h-64" data-testid="qr-code-image" />
            </div>

            <p className="text-muted-foreground font-ibm text-base">
              Open the Mi-Token Authenticator app and scan the QR code
            </p>

            <Button
              onClick={handleFinish}
              className="h-12 px-8 font-ibm font-medium"
              data-testid="finish-button"
            >
              Finish
            </Button>
          </div>
        </Card>
      )}

      {/* Email Input */}
      {currentStep === 3 && deliveryMethod === 'email' && !showEmailConfirm && (
        <Card className="p-8 border border-border" data-testid="email-input">
          <div className="space-y-6">
            <div>
              <h2 className="font-outfit font-bold text-2xl text-foreground mb-2">Enter Email Address</h2>
              <p className="text-muted-foreground font-ibm">
                We'll send the activation link to this email address
              </p>
            </div>

            <div>
              <Label htmlFor="email" className="font-ibm font-medium text-base mb-2">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="h-12 font-ibm"
                data-testid="email-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeliveryMethod(null)}
                className="h-12 font-ibm"
                data-testid="back-to-options"
                disabled={loading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSendEmail}
                className="flex-1 h-12 font-ibm font-medium"
                data-testid="send-email-button"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Activation Link'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Token Creation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel? Your progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-no">No, Continue</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} data-testid="cancel-dialog-yes">
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Sent Confirmation */}
      <AlertDialog open={showEmailConfirm} onOpenChange={setShowEmailConfirm}>
        <AlertDialogContent data-testid="email-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Activation Link Sent!</AlertDialogTitle>
            <AlertDialogDescription>
              The token activation link has been sent to {email}. Please check your email and follow the instructions to activate your soft-token.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={confirmEmailSent} data-testid="email-confirm-ok">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
